import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  CalendarRange, RefreshCw, AlertTriangle, CheckCircle, Eye
} from 'lucide-react';
import { format, addDays, addMonths } from 'date-fns';
import { toast } from 'sonner';

const WEEKDAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' }
];

const REPEAT_PATTERNS = [
  { value: 'weekdays', label: 'Every Weekday (Mon-Fri)' },
  { value: 'saturdays', label: 'Saturdays Only' },
  { value: 'selected_days', label: 'Selected Days Only' },
  { value: 'daily', label: 'Every Day' }
];

export default function BulkPhysicianDialog({ 
  open, 
  onOpenChange, 
  onSuccess,
  currentDate,
  physicians = [],
  fairnessStats = null
}) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    physician_id: '',
    event_type: 'PHYSICIAN_STANDARD',
    start_date: format(currentDate || new Date(), 'yyyy-MM-dd'),
    end_date: format(addMonths(currentDate || new Date(), 2), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    repeat_pattern: 'weekdays',
    selected_days: [0, 1, 2, 3, 4],
    overwrite_existing: false,
    notes: ''
  });

  useEffect(() => {
    if (open) {
      setPreviewData(null);
      setShowPreview(false);
      setFormData(prev => ({
        ...prev,
        physician_id: fairnessStats?.suggested_next || physicians[0]?.id || '',
        start_date: format(currentDate || new Date(), 'yyyy-MM-dd'),
        end_date: format(addMonths(currentDate || new Date(), 2), 'yyyy-MM-dd')
      }));
    }
  }, [open, currentDate, physicians, fairnessStats]);

  useEffect(() => {
    // Update times based on event type
    if (formData.event_type === 'PHYSICIAN_STANDARD') {
      setFormData(prev => ({ ...prev, start_time: '09:00', end_time: '17:00', repeat_pattern: 'weekdays' }));
    } else if (formData.event_type === 'PHYSICIAN_AFTERHOURS_WEEKDAY') {
      setFormData(prev => ({ ...prev, start_time: '17:00', end_time: '19:30', repeat_pattern: 'weekdays' }));
    } else if (formData.event_type === 'PHYSICIAN_AFTERHOURS_SATURDAY') {
      setFormData(prev => ({ ...prev, start_time: '09:00', end_time: '12:00', repeat_pattern: 'saturdays' }));
    }
  }, [formData.event_type]);

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      selected_days: prev.selected_days.includes(day)
        ? prev.selected_days.filter(d => d !== day)
        : [...prev.selected_days, day].sort()
    }));
  };

  const handlePreview = async () => {
    if (!formData.physician_id) {
      toast.error('Please select a physician');
      return;
    }
    
    setPreviewing(true);
    try {
      const res = await api.post('/schedules/bulk/physician/preview', formData);
      setPreviewData(res.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.physician_id) {
      toast.error('Please select a physician');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/schedules/bulk/physician', formData);
      toast.success(`Created ${res.data.created} schedule entries`);
      if (res.data.skipped > 0) {
        toast.info(`Skipped ${res.data.skipped} dates due to conflicts`);
      }
      setPreviewData(null);
      setShowPreview(false);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedules');
    } finally {
      setLoading(false);
    }
  };

  const selectedPhysician = physicians.find(p => p.id === formData.physician_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-teal-600" />
            Bulk Physician Scheduling
          </DialogTitle>
          <DialogDescription>
            Schedule a physician across multiple dates at once. Perfect for planning 2-3 months of coverage.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          {!showPreview ? (
            <div className="space-y-6 py-4">
              {/* Physician Selection */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <div className="space-y-2">
                  <Label className="text-base font-semibold">1. Select Physician *</Label>
                  <Select 
                    value={formData.physician_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, physician_id: v }))}
                  >
                    <SelectTrigger data-testid="bulk-physician-select">
                      <SelectValue placeholder="Choose a physician" />
                    </SelectTrigger>
                    <SelectContent>
                      {physicians.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          Dr. {p.last_name}
                          {fairnessStats?.suggested_next === p.id && ' ⭐ (Fewest after-hours)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedPhysician && (
                    <p className="text-sm text-slate-600">
                      Selected: <strong>Dr. {selectedPhysician.first_name} {selectedPhysician.last_name}</strong>
                    </p>
                  )}
                </div>
              </div>

              {/* Schedule Type */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <Label className="text-base font-semibold">2. Schedule Type</Label>
                <Select 
                  value={formData.event_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, event_type: v }))}
                >
                  <SelectTrigger data-testid="bulk-event-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PHYSICIAN_STANDARD">Standard Hours (9:00 AM - 5:00 PM)</SelectItem>
                    <SelectItem value="PHYSICIAN_AFTERHOURS_WEEKDAY">Weekday After-Hours (5:00 PM - 7:30 PM)</SelectItem>
                    <SelectItem value="PHYSICIAN_AFTERHOURS_SATURDAY">Saturday After-Hours (9:00 AM - 12:00 PM)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input 
                      type="time" 
                      value={formData.start_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input 
                      type="time" 
                      value={formData.end_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Date Range */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <Label className="text-base font-semibold">3. Date Range</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input 
                      type="date" 
                      value={formData.start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                      data-testid="bulk-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input 
                      type="date" 
                      value={formData.end_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                      data-testid="bulk-end-date"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      end_date: format(addMonths(new Date(prev.start_date), 1), 'yyyy-MM-dd')
                    }))}
                  >
                    1 Month
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      end_date: format(addMonths(new Date(prev.start_date), 2), 'yyyy-MM-dd')
                    }))}
                  >
                    2 Months
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      end_date: format(addMonths(new Date(prev.start_date), 3), 'yyyy-MM-dd')
                    }))}
                  >
                    3 Months
                  </Button>
                </div>
              </div>

              {/* Repeat Pattern */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <Label className="text-base font-semibold">4. Repeat Pattern</Label>
                <Select 
                  value={formData.repeat_pattern} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, repeat_pattern: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REPEAT_PATTERNS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {formData.repeat_pattern === 'selected_days' && (
                  <div className="space-y-2">
                    <Label>Select Days</Label>
                    <div className="flex gap-2 flex-wrap">
                      {WEEKDAYS.map(day => (
                        <Button
                          key={day.value}
                          type="button"
                          variant={formData.selected_days.includes(day.value) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleDayToggle(day.value)}
                          className={formData.selected_days.includes(day.value) ? 'bg-teal-600' : ''}
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Options */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <Label className="text-base font-semibold">5. Options</Label>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Overwrite Existing Schedules</p>
                    <p className="text-xs text-slate-500">Replace if dates are already occupied</p>
                  </div>
                  <Switch 
                    checked={formData.overwrite_existing}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, overwrite_existing: v }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Input 
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add notes for all entries..."
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Preview View */
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{previewData?.total_dates || 0}</p>
                  <p className="text-sm text-slate-500">Total Dates</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-600">{previewData?.will_create || 0}</p>
                  <p className="text-sm text-slate-500">Will Create</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{previewData?.conflicts?.length || 0}</p>
                  <p className="text-sm text-slate-500">Conflicts</p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-medium text-blue-700">Summary</p>
                <p className="text-sm text-blue-600 mt-1">
                  <strong>{previewData?.physician}</strong> will be scheduled for <strong>{previewData?.event_type?.replace('PHYSICIAN_', '').replace(/_/g, ' ')}</strong> from {previewData?.date_range}
                </p>
              </div>

              {previewData?.conflicts?.length > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <p className="font-medium text-amber-700">
                      {formData.overwrite_existing ? 'Will Overwrite:' : 'Will Skip (Conflicts):'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-[100px] overflow-y-auto">
                    {previewData.conflicts.slice(0, 20).map(date => (
                      <Badge key={date} variant="outline" className="bg-white text-xs">
                        {date}
                      </Badge>
                    ))}
                    {previewData.conflicts.length > 20 && (
                      <Badge variant="outline">+{previewData.conflicts.length - 20} more</Badge>
                    )}
                  </div>
                </div>
              )}

              <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Time</th>
                      <th className="p-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData?.entries?.slice(0, 30).map((entry, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{entry.date}</td>
                        <td className="p-2">{entry.start_time} - {entry.end_time}</td>
                        <td className="p-2">
                          {entry.has_conflict ? (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              {formData.overwrite_existing ? 'Overwrite' : 'Skip'}
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Create</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData?.entries?.length > 30 && (
                  <p className="p-2 text-center text-sm text-slate-500 bg-slate-50">
                    Showing 30 of {previewData.entries.length} entries
                  </p>
                )}
              </div>
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="mt-4 pt-4 border-t">
          {!showPreview ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                variant="outline"
                onClick={handlePreview}
                disabled={previewing || !formData.physician_id}
                data-testid="preview-bulk-btn"
              >
                {previewing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview
              </Button>
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={handleCreate}
                disabled={loading || !formData.physician_id}
                data-testid="create-bulk-btn"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Create Without Preview
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Back to Edit
              </Button>
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={handleCreate}
                disabled={loading}
                data-testid="confirm-bulk-btn"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Confirm & Create {previewData?.will_create || 0} Schedules
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
